import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { rxResource } from '@angular/core/rxjs-interop'
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { ButtonModule } from 'primeng/button'
import { Paginator, PaginatorState } from 'primeng/paginator'
import { TooltipModule } from 'primeng/tooltip'
import { AircraftLimit, AircraftLimitForm, AviationLiabilitiesForm } from '@features/insured/models/coverages/base-coverage.model'
import { CoverageFormService } from '@features/insured/services/coverages/coverage-form.service'
import { CodeByTypeApiResponse } from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { AlbertLimitComponent } from '@shared/components/albert-limit/albert-limit.component'
import { environment } from '@environments/environment'
import { formRevision, hasActualValue } from '@core/utils/form-utils'
import { isStartedAircraftLimit } from '@features/insured/utils/aircraft-limit.util'
import { AIRCRAFT_LIMIT_CONSISTENCY_API_RESPONSE } from '@features/insured/models/coverages/coverage-api.model'

type WarExcessForm = AviationLiabilitiesForm['aviationThirdPartyWarExcessLiability']

const ALL_AIRCRAFT_TYPES = 'One Fits All'

/** One line of the two columns; the entries of a line are unrelated, they only share the line. */
export interface AircraftLimitRow {
  index: number
  /** Disabled copy of an Origami entry, `null` once the Origami list has ended. */
  origami: FormGroup<AircraftLimitForm> | null
  /** An entry entered in Albert, `null` once those have ended. */
  override: FormGroup<AircraftLimitForm> | null
}

/**
 * The war excess limits per aircraft type: Origami's entries, derived from the fleet list, on the left and the
 * entries entered in Albert on the right. As soon as there are entries in Albert, they replace Origami's as a whole;
 * both lists come sorted by aircraft type, then limit.
 */
@Component({
  selector: 'albert-aircraft-limit-list',
  imports: [ReactiveFormsModule, ButtonModule, Paginator, TooltipModule, AlbertSelectComponent, AlbertLimitComponent],
  templateUrl: './aircraft-limit-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AircraftLimitListComponent {
  public readonly form = input.required<WarExcessForm>()
  public readonly canEdit = input(false)
  public readonly rowsPerPage = 5
  public readonly first = signal(0)
  public readonly pagedRows = computed(() => this.rows().slice(this.first(), this.first() + this.rowsPerPage))
  // Form arrays are no signals: bumped on every change, so the rows follow loads, additions and removals.
  private readonly revision = formRevision(this.form)
  private readonly origami = computed(() => {
    this.revision()
    return this.form().controls.origamiAircraftLimits.value
  })
  /** The backend derives the consistency from the entries in Albert as soon as there are any, so only without them it describes Origami's. */
  private readonly isConsistent = computed(() => {
    this.revision()
    return this.form().controls.aircraftLimitConsistency.value === AIRCRAFT_LIMIT_CONSISTENCY_API_RESPONSE.CONSISTENT
      && this.overrides.length === 0
      && this.origami().length > 0
  })
  /** A consistent fleet shares one limit, shown once for all aircraft types instead of per type. */
  private readonly origamiForms = computed(() => {
    const origami = this.origami()
    const shared = origami[0]
    if (this.isConsistent() && shared) {
      return [this.toDisabledForm({ uuid: null, aircraftType: ALL_AIRCRAFT_TYPES, limit: { ...shared.limit } })]
    }
    return origami.map(entry => this.toDisabledForm(entry))
  })
  public readonly rows = computed<AircraftLimitRow[]>(() => {
    this.revision()
    const origami = this.origamiForms()
    const overrides = this.overrides.controls
    return Array.from({ length: Math.max(origami.length, overrides.length) }, (_, index) => ({
      index,
      origami: origami[index] ?? null,
      override: overrides[index] ?? null
    }))
  })
  private readonly coverageFormService = inject(CoverageFormService)
  private readonly http = inject(HttpClient)
  private readonly aircraftTypes = rxResource({
    stream: () => this.http.get<CodeByTypeApiResponse[]>(`${environment.referenceApiUrl}/codeByType`, {
      params: { type: 'AIRCRAFT' }
    })
  })
  /** Reference aircraft types plus the fleet's and the entered ones, which not need to be in the reference list. */
  public readonly aircraftTypeOptions = computed(() => {
    this.revision()
    const types = [
      ...(this.aircraftTypes.value() ?? []).map(code => code.description),
      ...this.origami().map(entry => entry.aircraftType),
      ...this.overrides.controls.map(entry => entry.controls.aircraftType.value)
    ].filter(hasActualValue)
    return [...new Set(types)].sort((a, b) => a.localeCompare(b))
  })
  /** The shared limit's type only exists on Origami's side, entries in Albert always name an aircraft type. */
  public readonly origamiAircraftTypeOptions = computed(() => this.isConsistent() ? [ALL_AIRCRAFT_TYPES] : this.aircraftTypeOptions())

  constructor() {
    // A shorter list (e.g. another policy loaded) must not leave the paginator behind its last page.
    effect(() => {
      const count = this.rows().length
      if (this.first() > 0 && this.first() >= count) {
        this.first.set(Math.max(0, Math.floor((count - 1) / this.rowsPerPage) * this.rowsPerPage))
      }
    })
  }

  private get overrides(): FormArray<FormGroup<AircraftLimitForm>> {
    return this.form().controls.aircraftLimits
  }

  public onPageChange(event: PaginatorState): void {
    this.first.set(event.first ?? 0)
  }

  /** Adds an entry at the end of the list and shows the page it is on. */
  public onAddAircraft(): void {
    this.overrides.push(this.coverageFormService.buildAircraftLimitForm())
    this.first.set(Math.floor((this.overrides.length - 1) / this.rowsPerPage) * this.rowsPerPage)
  }

  public onClearAircraftType(row: AircraftLimitRow): void {
    this.clear(row, override => override.controls.aircraftType.setValue(null))
  }

  public onClearLimit(row: AircraftLimitRow): void {
    this.clear(row, override => override.controls.limit.setValue({ value: null, currency: null }))
  }

  /** An entry left without any value is removed; once none are left, Origami's entries apply again. */
  private clear(row: AircraftLimitRow, clearField: (override: FormGroup<AircraftLimitForm>) => void): void {
    const override = row.override
    if (!override) {
      return
    }
    // Marked dirty first: the autosave only picks up a change it sees on an already dirty form.
    override.markAsDirty()
    clearField(override)
    if (!isStartedAircraftLimit(override.getRawValue())) {
      this.overrides.markAsDirty()
      this.overrides.removeAt(row.index)
    }
  }

  private toDisabledForm(entry: AircraftLimit): FormGroup<AircraftLimitForm> {
    const group = this.coverageFormService.buildAircraftLimitForm()
    group.setValue(entry)
    group.disable({ emitEvent: false })
    return group
  }
}
